import { useEffect, useRef, useState } from 'react';
import { uploadNoticeImage } from '../lib/api';
import { sanitizeNoticeHtml } from '../driver/NoticeBody';

/**
 * 공지 본문 편집기(2026-10-13 way 「이미지 복붙 — 본문에 바로 보이도록 인라인 · 이미지 첨부 · 링크 추가」).
 *
 * 글자칸(textarea) 대신 보이는 그대로 고치는 칸(contentEditable). 저장 값은 HTML 이고 기사앱은 `NoticeBody` 가
 * DOMPurify 로 걸러 그린다(옛 버스인 공지와 같은 길).
 *  - 사진: 붙여넣기(캡처 → Ctrl+V)·끌어놓기·「사진 넣기」 → 올린 뒤 그 자리에 `<a href><img></a>`(누르면 크게).
 *  - 링크: 글자를 고르고 「링크 넣기」 → 그 글자가 링크. 고른 글자가 없으면 주소 그대로 링크로 넣는다.
 *  - 글 붙여넣기는 **글자만**(엑셀·웹 서식이 섞여 들어와 기사 화면이 깨지지 않게).
 * 🔴 처음 값은 **한 번만** 넣는다 — 입력마다 innerHTML 을 다시 쓰면 커서가 맨 앞으로 튄다(부모는 다 불러온 뒤 그린다).
 */

const HTML_RE = /<[a-z][a-z0-9]*[\s>/]/i;
const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** 웹에서 쓴 글자만 공지(줄바꿈) → HTML. 옛 HTML 공지는 거른 뒤 그대로. */
export function noticeToEditorHtml(text: string): string {
  if (!text) return '';
  if (HTML_RE.test(text)) return sanitizeNoticeHtml(text);
  return esc(text).replace(/\r?\n/g, '<br>');
}

/** 글자도 사진도 없으면 빈 값 — 「내용을 입력해 주세요」 검사가 `<br>` 하나에 속지 않게. */
function readValue(el: HTMLElement): string {
  const hasText = (el.textContent ?? '').trim().length > 0;
  const hasMedia = !!el.querySelector('img, table');
  return hasText || hasMedia ? el.innerHTML : '';
}

export default function NoticeEditor({
  initial,
  onChange,
  disabled,
}: {
  initial: string;
  onChange: (html: string) => void;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const range = useRef<Range | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = noticeToEditorHtml(initial);
    // 처음 한 번만(위 주석).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 칸 안의 커서를 기억해 둔다 — 버튼을 누르면 칸에서 포커스가 빠지므로. */
  function remember() {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current?.contains(sel.anchorNode)) range.current = sel.getRangeAt(0).cloneRange();
  }

  function restore() {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel) return;
    sel.removeAllRanges();
    if (range.current) sel.addRange(range.current);
    else {
      const r = document.createRange();
      r.selectNodeContents(el);
      r.collapse(false);
      sel.addRange(r);
    }
  }

  function insertHtml(html: string) {
    restore();
    document.execCommand('insertHTML', false, html);
    remember();
    if (ref.current) onChange(readValue(ref.current));
  }

  async function addImages(files: File[]) {
    const imgs = files.filter((f) => f.type.startsWith('image/'));
    if (!imgs.length) return;
    setError(null);
    setUploading((n) => n + imgs.length);
    for (const f of imgs) {
      try {
        const { url } = await uploadNoticeImage(f);
        const u = esc(url);
        insertHtml(`<p><a href="${u}"><img src="${u}" alt=""></a></p>`);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setUploading((n) => n - 1);
      }
    }
  }

  function onPaste(e: React.ClipboardEvent<HTMLDivElement>) {
    const files = Array.from(e.clipboardData.files ?? []);
    const text = e.clipboardData.getData('text/plain');
    // 엑셀·한글에서 복사하면 글자와 함께 그림판 사진도 실려 온다 — 글자가 있으면 글자가 우선.
    if (!text.trim() && files.some((f) => f.type.startsWith('image/'))) {
      e.preventDefault();
      remember();
      void addImages(files);
      return;
    }
    // 글은 글자만(서식 없이).
    e.preventDefault();
    document.execCommand('insertText', false, text);
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    const files = Array.from(e.dataTransfer.files ?? []);
    if (!files.some((f) => f.type.startsWith('image/'))) return;
    e.preventDefault();
    // 놓은 자리에 넣는다.
    const pos = (document as Document & { caretRangeFromPoint?: (x: number, y: number) => Range | null }).caretRangeFromPoint?.(e.clientX, e.clientY);
    if (pos) range.current = pos;
    void addImages(files);
  }

  function applyLink() {
    let url = linkUrl.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
    const selected = range.current && !range.current.collapsed && range.current.toString().trim();
    if (selected) {
      restore();
      document.execCommand('createLink', false, url);
      remember();
      if (ref.current) onChange(readValue(ref.current));
    } else {
      insertHtml(`<a href="${esc(url)}">${esc(url)}</a>&nbsp;`);
    }
    setLinkUrl('');
    setLinkOpen(false);
  }

  return (
    <div className="space-y-2">
      {!disabled && (
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className="btn-ghost px-2 py-1 text-xs" onMouseDown={(e) => e.preventDefault()} onClick={() => { remember(); fileRef.current?.click(); }}>
            🖼 사진 넣기
          </button>
          <button type="button" className="btn-ghost px-2 py-1 text-xs" onMouseDown={(e) => e.preventDefault()} onClick={() => { remember(); setLinkOpen((v) => !v); }}>
            🔗 링크 넣기
          </button>
          <span className="text-xs text-ink-faint">캡처한 사진은 내용 칸에 바로 붙여넣기(Ctrl+V)하셔도 됩니다.</span>
          {uploading > 0 && <span className="text-xs text-brand-600">사진 올리는 중… ({uploading})</span>}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              e.target.value = '';
              void addImages(files);
            }}
          />
        </div>
      )}
      {linkOpen && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-canvas p-2">
          <input
            className="field max-w-md flex-1 py-1 text-sm"
            placeholder="https://… (먼저 글자를 고르면 그 글자가 링크가 됩니다)"
            value={linkUrl}
            autoFocus
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); applyLink(); }
            }}
          />
          <button type="button" className="btn-primary px-3 py-1 text-xs" onClick={applyLink} disabled={!linkUrl.trim()}>
            넣기
          </button>
          <button type="button" className="btn-ghost px-3 py-1 text-xs" onClick={() => setLinkOpen(false)}>
            닫기
          </button>
        </div>
      )}
      <div
        ref={ref}
        className="field notice-body min-h-[16rem] max-h-[60vh] overflow-y-auto font-sans leading-relaxed"
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={(e) => onChange(readValue(e.currentTarget))}
        onKeyUp={remember}
        onMouseUp={remember}
        onBlur={remember}
        onPaste={onPaste}
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
